import { useCallback } from 'react'
import log from 'loglevel'
import {
  Int32Value,
  StringValue,
} from 'google-protobuf/google/protobuf/wrappers_pb'
import { ControlServicePromiseClient } from '@trustero/trustero-api-web/lib/model/control_grpc_web_pb'
import {
  AddControlPolicyRequest,
  CONTROL_STATUS,
  ControlStatusValue,
  CreateControlTestProcedureRequest,
  DeleteControlTestProcedureRequest,
  RemoveControlPolicyRequest,
  UpdateControlFieldsRequest,
  UpdateControlSuggestedEvidenceRequest,
  UpdateControlSuggestedTestsRequest,
  RegenerateControlSuggestionRequest,
  RegenerateControlOperationalRequest,
  UpdateControlPolicyRequest,
  UpdateControlFrameworksRequest,
  RefreshControlOperationalRequest,
  UpdateControlObjectiveRequest,
  UpdateControlProcedureRequest,
} from '@trustero/trustero-api-web/lib/model/control_pb'
import {
  RESPONSIBILITY,
  ResponsibilityValue,
} from '@trustero/trustero-api-web/lib/common/model_pb'
import { useAuthorizedGrpcClient } from '../../../../adapter'
import { useGrpcRevalidateByMethod } from '../../useGrpcMutate'
import { useInvalidateControlsCache } from './useControls'
import { useRefreshAfterTPRSave } from './useControlVersions'

export const useUpdateControlFields = (): ((args: {
  controlId: string
  objective?: string
  procedure?: string
  note?: string
  status?: CONTROL_STATUS
  ownerEmail?: string
  responsibility?: RESPONSIBILITY
  notApplicableReason?: string
  vendorId?: string
  name?: string
  modelId?: string
  customStatusId?: string
}) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ControlServicePromiseClient)
  const invalidate = useInvalidateControlsCache()

  return useCallback(
    async ({
      controlId,
      objective,
      procedure,
      note,
      status,
      ownerEmail,
      responsibility,
      notApplicableReason,
      vendorId,
      name,
      modelId,
      customStatusId,
    }) => {
      try {
        const request = new UpdateControlFieldsRequest().setControlId(controlId)
        if (objective !== undefined) {
          request.setObjective(new StringValue().setValue(objective))
        }
        if (procedure !== undefined) {
          request.setProcedure(new StringValue().setValue(procedure))
        }
        if (note !== undefined) {
          request.setNote(new StringValue().setValue(note))
        }
        if (status !== undefined) {
          request.setStatus(new ControlStatusValue().setValue(status))
        }
        if (ownerEmail !== undefined) {
          request.setOwnerEmail(new StringValue().setValue(ownerEmail))
        }
        if (responsibility !== undefined) {
          request.setResponsibility(
            new ResponsibilityValue().setValue(responsibility),
          )
        }
        if (notApplicableReason !== undefined) {
          request.setNotApplicableReason(
            new StringValue().setValue(notApplicableReason),
          )
        }
        if (vendorId !== undefined) {
          request.setVendorId(new StringValue().setValue(vendorId))
        }
        if (name !== undefined) {
          request.setName(new StringValue().setValue(name))
        }
        if (modelId !== undefined) {
          request.setModelId(new StringValue().setValue(modelId))
        }
        if (customStatusId !== undefined) {
          request.setCustomStatusId(new StringValue().setValue(customStatusId))
        }
        await client.updateControlFields(request)
      } catch (error) {
        log.error('Error updating control fields', error)
        throw error
      } finally {
        await invalidate()
      }
    },
    [client, invalidate],
  )
}

export const useUpdateControlSuggestedEvidence = (): ((args: {
  controlId: string
  suggestedEvidence: string
}) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ControlServicePromiseClient)
  const invalidate = useInvalidateControlsCache()

  return useCallback(
    async ({ controlId, suggestedEvidence }) => {
      try {
        const request = new UpdateControlSuggestedEvidenceRequest()
          .setControlId(controlId)
          .setSuggestedEvidence(suggestedEvidence)
        await client.updateControlSuggestedEvidence(request)
      } catch (error) {
        log.error('Error updating suggested evidence', error)
        throw error
      } finally {
        await invalidate()
      }
    },
    [client, invalidate],
  )
}

export const useUpdateControlSuggestedTests = (): ((args: {
  controlId: string
  suggestedTests: string
  testProcedureId?: string
  title?: string
  position?: number
}) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ControlServicePromiseClient)
  const refreshAfterTPRSave = useRefreshAfterTPRSave()

  return useCallback(
    async ({ controlId, suggestedTests, testProcedureId, title, position }) => {
      try {
        const request = new UpdateControlSuggestedTestsRequest()
          .setControlId(controlId)
          .setSuggestedTests(suggestedTests)
        if (testProcedureId) {
          request.setTestProcedureId(
            new StringValue().setValue(testProcedureId),
          )
        }
        if (title !== undefined) {
          request.setTitle(new StringValue().setValue(title))
        }
        if (position !== undefined) {
          request.setPosition(new Int32Value().setValue(position))
        }
        await client.updateControlSuggestedTests(request)
      } catch (error) {
        log.error('Error updating suggested tests', error)
        throw error
      } finally {
        await refreshAfterTPRSave()
      }
    },
    [client, refreshAfterTPRSave],
  )
}

export const useRegenerateControlSuggestedEvidence = (): ((
  controlId: string,
) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ControlServicePromiseClient)
  const invalidate = useInvalidateControlsCache()

  return useCallback(
    async (controlId: string) => {
      try {
        const request = new RegenerateControlSuggestionRequest().setControlId(
          controlId,
        )
        await client.regenerateControlSuggestedEvidence(request)
      } catch (error) {
        log.error('Error regenerating suggested evidence', error)
        throw error
      } finally {
        await invalidate()
      }
    },
    [client, invalidate],
  )
}

export const useRegenerateControlSuggestedTests = (): ((
  controlId: string,
) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ControlServicePromiseClient)
  const invalidate = useInvalidateControlsCache()

  return useCallback(
    async (controlId: string) => {
      try {
        const request = new RegenerateControlSuggestionRequest().setControlId(
          controlId,
        )
        await client.regenerateControlSuggestedTests(request)
      } catch (error) {
        log.error('Error regenerating suggested tests', error)
        throw error
      } finally {
        await invalidate()
      }
    },
    [client, invalidate],
  )
}

export const useRegenerateControlSuggestedOperational = (): ((
  controlId: string,
  complianceFrameworkBaseModelIds: string[],
) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ControlServicePromiseClient)
  const invalidate = useInvalidateControlsCache()

  return useCallback(
    async (
      controlId: string,
      complianceFrameworkBaseModelIds: string[],
    ) => {
      try {
        const request = new RegenerateControlOperationalRequest()
          .setControlId(controlId)
          .setComplianceFrameworkBaseModelIdsList(
            complianceFrameworkBaseModelIds,
          )
        await client.regenerateControlSuggestedOperational(request)
      } catch (error) {
        log.error('Error regenerating suggested operational', error)
        throw error
      } finally {
        await invalidate()
      }
    },
    [client, invalidate],
  )
}

export const useUpdateControlPolicy = (): ((args: {
  controlId: string
  policyModelId?: string
  skipGlobalControlsInvalidate?: boolean
}) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ControlServicePromiseClient)
  const invalidate = useInvalidateControlsCache()
  const mutateMethod = useGrpcRevalidateByMethod()

  return useCallback(
    async ({ controlId, policyModelId, skipGlobalControlsInvalidate }) => {
      try {
        const request = new UpdateControlPolicyRequest().setControlId(controlId)
        if (policyModelId !== undefined) {
          request.setPolicyModelId(new StringValue().setValue(policyModelId))
        }
        await client.updateControlPolicy(request)
      } catch (error) {
        log.error('Error updating control policy', error)
        throw error
      } finally {
        if (!skipGlobalControlsInvalidate) {
          await invalidate()
        }
        await Promise.all([
          mutateMethod(ControlServicePromiseClient.prototype.listControlPolicies),
          mutateMethod(
            ControlServicePromiseClient.prototype.batchListControlPolicies,
          ),
        ])
      }
    },
    [client, invalidate, mutateMethod],
  )
}

export const useAddControlPolicy = (): ((args: {
  controlId: string
  policyModelId: string
  skipGlobalControlsInvalidate?: boolean
}) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ControlServicePromiseClient)
  const invalidate = useInvalidateControlsCache()
  const mutateMethod = useGrpcRevalidateByMethod()

  return useCallback(
    async ({ controlId, policyModelId, skipGlobalControlsInvalidate }) => {
      try {
        const request = new AddControlPolicyRequest()
          .setControlId(controlId)
          .setPolicyModelId(policyModelId)
        await client.addControlPolicy(request)
      } catch (error) {
        log.error('Error adding control policy', error)
        throw error
      } finally {
        if (!skipGlobalControlsInvalidate) {
          await invalidate()
        }
        await Promise.all([
          mutateMethod(ControlServicePromiseClient.prototype.listControlPolicies),
          mutateMethod(
            ControlServicePromiseClient.prototype.batchListControlPolicies,
          ),
        ])
      }
    },
    [client, invalidate, mutateMethod],
  )
}

export const useRemoveControlPolicy = (): ((args: {
  controlId: string
  policyModelId: string
  skipGlobalControlsInvalidate?: boolean
}) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ControlServicePromiseClient)
  const invalidate = useInvalidateControlsCache()
  const mutateMethod = useGrpcRevalidateByMethod()

  return useCallback(
    async ({ controlId, policyModelId, skipGlobalControlsInvalidate }) => {
      try {
        const request = new RemoveControlPolicyRequest()
          .setControlId(controlId)
          .setPolicyModelId(policyModelId)
        await client.removeControlPolicy(request)
      } catch (error) {
        log.error('Error removing control policy', error)
        throw error
      } finally {
        if (!skipGlobalControlsInvalidate) {
          await invalidate()
        }
        await Promise.all([
          mutateMethod(ControlServicePromiseClient.prototype.listControlPolicies),
          mutateMethod(
            ControlServicePromiseClient.prototype.batchListControlPolicies,
          ),
        ])
      }
    },
    [client, invalidate, mutateMethod],
  )
}

export const useUpdateControlFrameworks = (): ((args: {
  controlId: string
  frameworkIds: string[]
  complianceFrameworkIds: string[]
}) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ControlServicePromiseClient)
  const invalidate = useInvalidateControlsCache()

  return useCallback(
    async ({ controlId, frameworkIds, complianceFrameworkIds }) => {
      try {
        const request = new UpdateControlFrameworksRequest()
          .setControlId(controlId)
          .setFrameworkIdsList(frameworkIds)
          .setComplianceFrameworkIdsList(complianceFrameworkIds)
        await client.updateControlFrameworks(request)
      } catch (error) {
        log.error('Error updating control frameworks', error)
        throw error
      } finally {
        await invalidate()
      }
    },
    [client, invalidate],
  )
}

export const useRefreshControlSuggestedOperational = (): ((
  controlId: string,
  complianceFrameworkBaseModelIds: string[],
) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ControlServicePromiseClient)
  const invalidate = useInvalidateControlsCache()

  return useCallback(
    async (
      controlId: string,
      complianceFrameworkBaseModelIds: string[],
    ) => {
      try {
        const request = new RefreshControlOperationalRequest()
          .setControlId(controlId)
          .setComplianceFrameworkBaseModelIdsList(
            complianceFrameworkBaseModelIds,
          )
        await client.refreshControlSuggestedOperational(request)
      } catch (error) {
        log.error('Error refreshing suggested operational', error)
        throw error
      } finally {
        await invalidate()
      }
    },
    [client, invalidate],
  )
}

export const useUpdateControlObjective = (): ((args: {
  controlId: string
  objective: string
}) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ControlServicePromiseClient)
  const invalidate = useInvalidateControlsCache()

  return useCallback(
    async ({ controlId, objective }) => {
      try {
        const request = new UpdateControlObjectiveRequest()
          .setControlId(controlId)
          .setObjective(objective)
        await client.updateControlObjective(request)
      } catch (error) {
        log.error('Error updating control objective', error)
        throw error
      } finally {
        await invalidate()
      }
    },
    [client, invalidate],
  )
}

export const useUpdateControlProcedure = (): ((args: {
  controlId: string
  procedure: string
}) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ControlServicePromiseClient)
  const invalidate = useInvalidateControlsCache()

  return useCallback(
    async ({ controlId, procedure }) => {
      try {
        const request = new UpdateControlProcedureRequest()
          .setControlId(controlId)
          .setProcedure(procedure)
        await client.updateControlProcedure(request)
      } catch (error) {
        log.error('Error updating control procedure', error)
        throw error
      } finally {
        await invalidate()
      }
    },
    [client, invalidate],
  )
}

export const useCreateControlTestProcedure = (): ((args: {
  controlId: string
  title: string
  body: string
}) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ControlServicePromiseClient)
  const refreshAfterTPRSave = useRefreshAfterTPRSave()

  return useCallback(
    async ({ controlId, title, body }) => {
      try {
        const request = new CreateControlTestProcedureRequest()
          .setControlId(controlId)
          .setTitle(title)
          .setBody(body)
        await client.createControlTestProcedure(request)
      } catch (error) {
        log.error('Error creating test procedure', error)
        throw error
      } finally {
        await refreshAfterTPRSave()
      }
    },
    [client, refreshAfterTPRSave],
  )
}

export const useDeleteControlTestProcedure = (): ((args: {
  controlId: string
  testProcedureId: string
}) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ControlServicePromiseClient)
  const refreshAfterTPRSave = useRefreshAfterTPRSave()

  return useCallback(
    async ({ controlId, testProcedureId }) => {
      try {
        const request = new DeleteControlTestProcedureRequest()
          .setControlId(controlId)
          .setTestProcedureId(testProcedureId)
        await client.deleteControlTestProcedure(request)
      } catch (error) {
        log.error('Error deleting test procedure', error)
        throw error
      } finally {
        await refreshAfterTPRSave()
      }
    },
    [client, refreshAfterTPRSave],
  )
}
